/* eslint-disable max-lines */
import { TranslocoService } from '@jsverse/transloco';
import isEmpty from 'lodash-es/isEmpty';
import { Observable, of, zip, iif } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import { ViewportScroller } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit, ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { differenceInDays } from 'date-fns';

import { isNullOrUndefined } from '@app/util/util';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { BillingFacade } from '@billing/billing.facade';
import { BookingFacade } from '@booking/booking.facade';
import { BOOKING_FORM_INPUTS } from '@booking/constants/forms.constants';
import { BookingBaseContainer } from '@booking/containers/booking-base.container';
import { BookingArrangementVM, SubmitBookingArrangement } from '@booking/entities/booking-arrangement';
import { autoBookingPayloadMapper } from '@booking/mappers/auto-booking.payload.mapper';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { TravelerInfoType, TravelerTypes } from '@pax/entities/traveler-info';
import { PaxFacade } from '@pax/pax.facade';
import { PaxFormService } from '@pax/services/pax-form.service';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { PROVIDERS } from '@booking/constants/providers.contants';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { BookingAmplitudeService } from '@booking/services/booking-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { AppFacade } from '@app/app.facade';
import {
  informativeBookingRoute, informativePaxCelebrateBookingRoute
} from '@booking/constants/booking-routes.constants';
import { AssistanceFacade } from '@shared/travel-assistance/assistance.facade';
import { DEFAULT_PHONE_CONTROL, DEFAULT_EMAIL_CONTROL } from '@forms/contact-form/constants/contact-form.constants';
import { FullValidTravelersPipe } from '@pax/pipes/full-valid-travelers.pipe';
import { AvoService } from '@avo/services/avo.service';
import { TravelerListVmPipe } from '@pax/pipes/traveler-list-vm.pipe';
import { RateUI } from '@autos/auto-results/components/auto-detail-rates/entities/rates';
import { mapPriceToRates } from '@autos/auto-results/components/auto-detail-rates/mappers/rates-mapper';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { assistanceSearchAmplitudePropsMapper } from '@app/modules/core/mappers/assistance-amplitude.mapper';
import { Price } from '@app/entities/price';
import { 
  TypographyColor, 
  TypographyFontFamily, 
  TypographyFontSize 
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-booking-autos',
  templateUrl: './booking-autos.container.html',
  styleUrls: ['../booking-base.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class BookingAutosContainer extends BookingBaseContainer
  implements OnInit, OnChanges, AfterViewInit, OnDestroy, AfterViewChecked {

  @Input() autoValidation: VehicleValidateResponse;
  @Input() companyCode: string;

  public bookingAutosForm: UntypedFormGroup;
  public travelType: TravelBookingType = TravelBookingType.AUTOS;
  public travelerInfoType: TravelerInfoType = TravelerInfoType.DRIVER;
  public adults: TravelerTypes = TravelerTypes.ADULTS;
  public policiesType: SearchBy = SearchBy.AUTOS;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly translateService: TranslocoService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdRef: ChangeDetectorRef,
    protected appFacade: AppFacade,
    protected viewportScroller: ViewportScroller,
    protected facade: BookingFacade,
    protected billingFacade: BillingFacade,
    protected paxFacade: PaxFacade,
    protected paxFormService: PaxFormService,
    protected scrollFormService: ScrollFormService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected bookingAmplitudeService: BookingAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected assistanceFacade: AssistanceFacade,
    protected fullValidTravelersPipe: FullValidTravelersPipe,
    protected avoService: AvoService,
    protected travelerListVmPipe: TravelerListVmPipe,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(
      route, router, cdRef, appFacade, viewportScroller, 
      facade, billingFacade, paxFacade, paxFormService, scrollFormService,
      propertiesAmplitudeService, bookingAmplitudeService, triggerAmplitudeService,
      assistanceFacade, fullValidTravelersPipe, avoService, travelerListVmPipe
    );
    this.travelerBookingProvider = TravelBookingType.AUTOS;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['user'] && this.bookingAutosForm) {
      this.setUserData();
    }
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.subscriptions.push(
        this.celebratePaxView$.pipe(
          distinctUntilChanged(),
          tap(celebratePaxView => {
            if (celebratePaxView) {
              this.router.navigate([{ outlets: { [informativeBookingRoute]: [informativePaxCelebrateBookingRoute] } }], {
                state: { celebratePaxView },
                relativeTo: this.route, skipLocationChange: true
              });
            }
          })
        ).subscribe()
      );
      this.createForm();
      this.isLoadingBookingDelay$ = this.isLoadingBookingDelaySubscription();
      this.listenFormChanges(this.bookingAutosForm);
      this.configDefaultByInfoType(this.travelerBookingProvider, this.startDate);
      this.listenToAssistanceSearchCall(
        this.autoValidation, 
        this.autoValidation?.VehicleOption?.Rates?.[0]?.Price?.CurrencyCode,
        this.route.snapshot.paramMap
      );
    }
  }

  ngAfterViewInit(): void {
    if (this.user && this.bookingAutosForm) this.setUserData();
  }

  ngAfterViewChecked(): void {
    this.checkPaxSection();
  }

  ngOnDestroy(): void {
    this.assistanceFacade.resetAssistanceSearchInfo();
    if (!isNullOrUndefined(this.isLoadingBookingDelay$)) {
      this.isLoadingBookingDelay$.unsubscribe();
    }
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get passengerArrangementsState$(): Observable<PassengerArrangement[]> {
    return this.facade.passengerArrangementsState$;
  }

  get isRentingCarz(): boolean {
    return this.autoValidation?.VehicleOption?.Provider === PROVIDERS.RENTING_CARTZ;
  }

  get hasAutoValidation(): boolean {
    return !isEmpty(this.autoValidation);
  }

  get amount(): Price {
    return (this.autoValidation.VehicleOption?.Rates && this.autoValidation.VehicleOption?.Rates.length > 0) ?
      this.autoValidation.VehicleOption.Rates[0].Price : {} as Price;
  }

  get payPrice(): number {
    return this.autoValidation?.VehicleOption?.Rates[0]?.TaxesPayableAtDestination ?
      this.autoValidation?.VehicleOption?.Rates[0]?.TotalPricePayNow : this.amount?.Total;
  }

  get contract(): string {
    return (this.autoValidation.VehicleOption.CommentContracts) ? this.autoValidation.VehicleOption.CommentContracts : null;
  }

  get payAtDestination(): number {
    return this.autoValidation?.VehicleOption?.Rates?.length &&
      this.autoValidation?.VehicleOption?.Rates[0]?.TaxesPayableAtDestination &&
      this.autoValidation?.VehicleOption?.Rates[0]?.Price?.Taxes ? 
      this.autoValidation.VehicleOption.Rates[0].Price.Taxes as number : 0;
  }

  get showPayDestination(): boolean {
    const price = (this.autoValidation?.VehicleOption?.Rates &&
      this.autoValidation?.VehicleOption?.Rates.length > 0 &&
      this.autoValidation?.VehicleOption?.Rates[0].Price?.Taxes) ?
      this.autoValidation?.VehicleOption?.Rates[0]?.Price?.Taxes as number : 0;
    return this.autoValidation.VehicleOption?.Rates[0].TaxesPayableAtDestination &&
      !!this.autoValidation.VehicleOption?.Rates[0].Price && price > 0;
  }

  get period(): string {
    const vehicle = this.autoValidation.VehicleOption;
    if (vehicle) {
      const diff = differenceInDays(new Date(vehicle.DropOff.Time), new Date(vehicle.PickUp.Time));
      return (diff > 1) ?
        this.translateService.translate('AUTOS_MODULE.RATES.DAYS_RENT', { period: diff }) :
        this.translateService.translate('AUTOS_MODULE.RATES.ONEDAY_RENT');
    }
    return '';
  }

  get rates(): RateUI[] {
    const vehicle = this.autoValidation.VehicleOption;
    if (vehicle && vehicle.Rates && vehicle.Rates.length > 0 && vehicle.Rates[0].Price) {
      return mapPriceToRates(vehicle.Rates[0].Price, this.payAtDestination, this.totalPricePayNow);
    }
    return [];
  }

  get totalPricePayNow(): number {
    return (this.autoValidation.VehicleOption.Rates[0].TaxesPayableAtDestination) ?
      this.autoValidation.VehicleOption.Rates[0].TotalPricePayNow : null;
  }

  get startDate(): string {
    return this.autoValidation?.VehicleOption?.PickUp?.Time;
  }

  public itemNumber$(index: number, previewTypes: string[] = []): Observable<number> {
    return this.passengerArrangementsState$
      .pipe(
        map(arrangement => {
          let item = index + 1;
          previewTypes.forEach(type => {
            item += arrangement.find(info => info.id === type).quantity;
          });
          return item;
        })
      );
  }

  public isRequiredAutoTraveler(index: number): boolean {
    return index === 0;
  }

  public passengerInfo$(type: string): Observable<number[]> {
    return this.passengerArrangementsState$.pipe(
      map(arrangement => new Array(arrangement.find(passenger => passenger.id === type).quantity).fill(0)));
  }

  public showAutoDetail(): void {
    this.router.navigate([{
      outlets: {
        'auto-detail': [this.bookingDetail]
      }
    }], {
      state: {
        isAutoDetail: true,
        autoValidation: this.autoValidation
      },
      relativeTo: this.route, skipLocationChange: true
    });
  }

  public submitBookingOptions(isDisabled: boolean): void {
    zip(
      this.submitToBookServiceSubscription$(this.bookingAutosForm.value, null),
      this.propertiesAmplitudeService.searchProperties$(SearchBy.AUTOS).pipe(take(1)),
      this.assistanceFacade.assistanceSearchInfo$
    ).pipe(
      switchMap((([{ props, trigger }, searchProps, assistanceSearchInfo]) => (
        this.avoService.getEventTriggerProps$(
          AVO_EVENTS.VIEW_TRAVEL_ASSISTANCE_MODAL,
          assistanceSearchAmplitudePropsMapper(assistanceSearchInfo?.Results?.[0]),
          searchProps
        ).pipe(
          tap(eventProps => {
            if (this.isActivePaidAssistance ) {
              this.fetchAssistanceModal(this.bookingAutosForm, eventProps, isDisabled);
            } else {
              this.submitByBookServiceAction({ props, trigger }, this.bookingAutosForm, this.travelerBookingProvider);
            }
          })
        )
      )))
    ).subscribe().unsubscribe();
  }

  public submitBookingOptionsByAssistance(
    arrangement: BookingArrangementVM, assistanceInfo: { optionId: number, searchId: string }
  ): void {
    this.submitToBookServiceSubscription$(arrangement, assistanceInfo).pipe(
      tap(({ props, trigger }) => this.submitByBookServiceAction({ props, trigger }, this.bookingAutosForm, this.travelType))
    ).subscribe().unsubscribe();
  }

  private createForm(): void {
    this.bookingAutosForm = new UntypedFormGroup({
      [BOOKING_FORM_INPUTS.TRAVELERS]: this.fb.array([]),
      [BOOKING_FORM_INPUTS.EMAIL]: DEFAULT_EMAIL_CONTROL,
      [BOOKING_FORM_INPUTS.PHONE]: DEFAULT_PHONE_CONTROL,
      [BOOKING_FORM_INPUTS.COMMENTS]: new UntypedFormControl('', [
        Validators.maxLength(500),
      ]),
      [BOOKING_FORM_INPUTS.TERMS]: new UntypedFormControl(null, [
        Validators.required,
        Validators.minLength(3)
      ]),
      [BOOKING_FORM_INPUTS.BILLING]: this.fb.group({})
    });
    if (this.individualTerms) {
      this.bookingAutosForm.addControl(BOOKING_FORM_INPUTS.EXTRA_TERMS, new UntypedFormControl(null, [
        Validators.required, Validators.minLength(3)
      ]));
    }
  }

  public setShowBillingModule(value: boolean): void {
    this.showBillingModule = value;
    this.resetbillingForm(this.bookingAutosForm);
  }

  private submitToBookServiceSubscription$(
    arrangement: BookingArrangementVM, assistanceInfo: { optionId: number, searchId: string }
  ): Observable<SubmitBookingArrangement> {
    return zip(
      this.route.paramMap.pipe(take(1)),
      this.billingFacade.defaultBillingInfo$.pipe(take(1)),
      this.propertiesAmplitudeService.searchProperties$(SearchBy.AUTOS).pipe(take(1)),
      this.bookingReady$.pipe(take(1))
    ).pipe(
      map(([params, defaultBillingInfo, searchProps, isReadyBook]) => ({
        params, defaultBillingInfo, searchProps,
        isReadyBook: !!this.bookingAutosForm.valid && !!this.hasAutoValidation && !!isReadyBook
      })),
      switchMap(({ isReadyBook, searchProps, params, defaultBillingInfo }) => iif(
        () => !!isReadyBook,
        zip(
          of(
            autoBookingPayloadMapper(
              this.autoValidation.ValidateId, arrangement, defaultBillingInfo, assistanceInfo
            )
          ),
          this.bookingAmplitudeService.autosCheckoutTriggerAmplitudeEntity$(this.autoValidation,
            params, searchProps).pipe(take(1))
        ),
        of([null, null])
      )),
      map(([props, trigger]) => ({ props, trigger }))
    );
  }

  private setUserData(): void {
    this.bookingAutosForm.patchValue({
      [BOOKING_FORM_INPUTS.EMAIL]: this.user?.email ? this.user.email : '',
      [BOOKING_FORM_INPUTS.PHONE]: this.user?.phone ? `${this.user?.country_code?.replace('+', '')}${this.user.phone}` : ''
    });
  }
}
